import type { PollInsights, Slot, SlotScore } from "./types";

export interface ConsensusFilterResult {
  /** Set of slot IDs that should remain visible */
  visibleSlotIds: Set<string>;
  /** Slot IDs masqués */
  hiddenSlotIds: string[];
}

/**
 * Filtre les créneaux refusés par >= `threshold` participants.
 *
 * Invariants (testés) :
 * - Le slot gagnant (`insights.winnerSlotId`) reste TOUJOURS visible,
 *   même s'il a `no >= threshold`. C'est une règle produit non négociable.
 * - `threshold = Infinity` désactive le filtre (tous visibles).
 * - `threshold <= 0` ou non fini → traité comme Infinity.
 * - Slots sans score connu restent visibles (fallback safe).
 */
export function applyConsensusFilter(
  slots: Slot[],
  insights: PollInsights,
  threshold: number,
): ConsensusFilterResult {
  const safeThreshold =
    Number.isFinite(threshold) && threshold > 0
      ? threshold
      : Number.POSITIVE_INFINITY;
  const scoreById = new Map<string, SlotScore>(
    insights.scores.map((s) => [s.slotId, s]),
  );
  const visibleSlotIds = new Set<string>();
  const hiddenSlotIds: string[] = [];
  for (const slot of slots) {
    const score = scoreById.get(slot.id);
    const isWinner = slot.id === insights.winnerSlotId;
    if (isWinner) {
      visibleSlotIds.add(slot.id);
      continue;
    }
    if (!score) {
      visibleSlotIds.add(slot.id);
      continue;
    }
    if (score.no >= safeThreshold) {
      hiddenSlotIds.push(slot.id);
    } else {
      visibleSlotIds.add(slot.id);
    }
  }
  return { visibleSlotIds, hiddenSlotIds };
}
